/**
 * Every change the Discipline page can make, against the hub (live) or the demo data
 * (?mock=1, where the same changes are simulated so every flow can be tried). Errors are
 * thrown as ApiError; the caller shows them.
 */
import { useMemo } from 'react';
import { disciplineRuleInfo } from '@orgus/protocol';
import type {
  DisciplineDriftSchedule,
  DisciplineDryRunResult,
  DisciplinePack,
  DisciplinePin,
  DisciplineSendBack,
  DisciplineState,
  DisciplineWaiver,
  Id,
  Violation,
} from '@orgus/protocol';
import { api, apiDelete, apiPost } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { CURATED, effectiveSettings, modelFromRaw } from './editor.ts';
import type { TaskDraft } from './feed.ts';
import { mockScan } from './mock.ts';

const BASE = '/api/ext/discipline';
const enc = encodeURIComponent;

export interface WaiverInput {
  projectId: Id;
  rule: string;
  path: string;
  reason: string;
  owner: string;
  expires: string;
}

export interface SendBackInput {
  projectId: Id;
  sessionId: Id;
  violations: Violation[];
  note: string;
}

export interface PinInput {
  pack: string;
  version: string;
  scope: 'all' | Id[];
  enforce: boolean;
}

export interface DisciplineActions {
  waive(w: WaiverInput): Promise<DisciplineWaiver>;
  revokeWaiver(id: Id): Promise<void>;
  sendBack(s: SendBackInput): Promise<DisciplineSendBack>;
  cancelSendBack(id: Id): Promise<void>;
  openTask(d: TaskDraft): Promise<{ id: Id }>;
  onboard(projectId: Id): Promise<void>;
  adopt(projectId: Id, use: 'suggested' | 'current'): Promise<void>;
  dropBaseline(projectId: Id): Promise<void>;
  dryRun(projectId: Id, text: string): Promise<DisciplineDryRunResult>;
  saveRulebook(projectId: Id, text: string): Promise<{ problems: string[] }>;
  dropRulebook(projectId: Id): Promise<void>;
  publishPack(p: Omit<DisciplinePack, 'kind' | 'publishedBy' | 'publishedAt'>): Promise<DisciplinePack>;
  pin(p: PinInput): Promise<void>;
  unpin(pack: string): Promise<void>;
  driftNow(projectId?: Id): Promise<void>;
  setDriftSchedule(s: DisciplineDriftSchedule): Promise<void>;
}

const live: DisciplineActions = {
  waive: (w) => apiPost<DisciplineWaiver>(`${BASE}/waivers`, w),
  revokeWaiver: async (id) => void (await apiDelete(`${BASE}/waivers/${enc(id)}`)),
  sendBack: (s) => apiPost<DisciplineSendBack>(`${BASE}/sendbacks`, s),
  cancelSendBack: async (id) => void (await apiPost(`${BASE}/sendbacks/${enc(id)}/cancel`)),
  openTask: (d) => apiPost<{ id: Id }>('/api/missions/items', d),
  onboard: async (projectId) => void (await apiPost(`${BASE}/onboard`, { projectId })),
  adopt: async (projectId, use) => void (await apiPost(`${BASE}/baseline`, { projectId, use })),
  dropBaseline: async (projectId) => void (await apiDelete(`${BASE}/baseline/${enc(projectId)}`)),
  dryRun: (projectId, text) => api<DisciplineDryRunResult>(`${BASE}/dryrun`, { method: 'POST', body: { projectId, text }, timeoutMs: 55_000 }),
  saveRulebook: (projectId, text) => api<{ problems: string[] }>(`${BASE}/rulebook/${enc(projectId)}`, { method: 'PUT', body: { text } }),
  dropRulebook: async (projectId) => void (await apiDelete(`${BASE}/rulebook/${enc(projectId)}`)),
  publishPack: (p) => apiPost<DisciplinePack>(`${BASE}/packs`, p),
  pin: async (p) => void (await apiPost(`${BASE}/pins`, p)),
  unpin: async (pack) => void (await apiDelete(`${BASE}/pins/${enc(pack)}`)),
  driftNow: async (projectId) => void (await apiPost(`${BASE}/drift`, projectId ? { projectId } : {})),
  setDriftSchedule: async (s) => void (await api(`${BASE}/drift/schedule`, { method: 'PUT', body: s })),
};

// ---------------------------------------------------------------------------
// Demo mode: the same changes, applied to the demo slice after a short pause.
// ---------------------------------------------------------------------------

const pause = (ms = 500) => new Promise((r) => setTimeout(r, ms));
const cur = () => useOrgus.getState().ext.discipline as DisciplineState | undefined;
const put = (next: DisciplineState) => useOrgus.getState().applyExt({ ext: 'discipline', type: 'state', data: next });
const update = (fn: (s: DisciplineState) => DisciplineState) => {
  const s = cur();
  if (s) put(fn(s));
};
let seq = 0;
const id = (p: string) => `${p}_demo${++seq}`;

/** A stable small number per rule, so the demo dry run looks the same each time. */
function demoFiles(rule: string): number {
  let h = 0;
  for (const c of rule) h = (h * 31 + c.charCodeAt(0)) % 997;
  return h % 5;
}

const mock: DisciplineActions = {
  async waive(w) {
    await pause();
    const out: DisciplineWaiver = { ...w, id: id('wvr'), createdBy: 'owner', createdAt: Date.now() };
    update((s) => ({ ...s, waivers: [...(s.waivers ?? []), out] }));
    return out;
  },
  async revokeWaiver(wid) {
    await pause();
    update((s) => ({ ...s, waivers: (s.waivers ?? []).map((w) => (w.id === wid ? { ...w, revokedAt: Date.now(), revokedBy: 'owner' } : w)) }));
  },
  async sendBack(input) {
    await pause();
    const now = Date.now();
    const sb: DisciplineSendBack = { ...input, id: id('sb'), machineId: useOrgus.getState().sessions[input.sessionId]?.machineId ?? '', state: 'pending', createdBy: 'owner', createdAt: now, updatedAt: now };
    update((s) => ({ ...s, sendbacks: [sb, ...(s.sendbacks ?? [])] }));
    // The session's hook picks it up a little later.
    setTimeout(() => update((s) => ({ ...s, sendbacks: (s.sendbacks ?? []).map((x) => (x.id === sb.id && x.state === 'pending' ? { ...x, state: 'delivered', updatedAt: Date.now() } : x)) })), 6000);
    return sb;
  },
  async cancelSendBack(sid) {
    await pause();
    update((s) => ({ ...s, sendbacks: (s.sendbacks ?? []).map((x) => (x.id === sid ? { ...x, state: 'cancelled', updatedAt: Date.now() } : x)) }));
  },
  async openTask() {
    await pause();
    return { id: id('itm') };
  },
  async onboard(projectId) {
    update((s) => ({ ...s, pending: { scans: [...(s.pending?.scans ?? []), projectId], drift: s.pending?.drift ?? [] } }));
    setTimeout(() => {
      update((s) => {
        const name = useOrgus.getState().projects[projectId]?.name;
        const r = s.reports.find((x) => x.projectId === projectId);
        const scan = mockScan(projectId, name, r?.machineId ?? '', Date.now(), r?.score ?? 100, r?.violations.length ?? 0);
        return { ...s, scans: [scan, ...(s.scans ?? []).filter((x) => x.projectId !== projectId)], pending: { scans: (s.pending?.scans ?? []).filter((x) => x !== projectId), drift: s.pending?.drift ?? [] } };
      });
    }, 1600);
  },
  async adopt(projectId, use) {
    await pause();
    update((s) => {
      const scan = s.scans?.find((x) => x.projectId === projectId);
      const total = use === 'suggested' ? (scan?.baseline.total ?? 0) : (scan?.current.total ?? 0);
      const now = Date.now();
      return {
        ...s,
        baselines: { ...s.baselines, [projectId]: { projectId, createdAt: now, createdBy: 'owner', total, remaining: total, byRule: use === 'suggested' ? (scan?.baseline.byRule ?? {}) : (scan?.current.byRule ?? {}) } },
        managed: use === 'suggested' && scan ? { ...s.managed, [projectId]: { projectId, text: scan.suggested.yaml, updatedBy: 'owner', updatedAt: now } } : s.managed ?? {},
        reports: s.reports.map((r) => (r.projectId === projectId ? { ...r, ratchet: { total, remaining: total, fresh: 0, burned: 0, since: now }, violations: r.violations.map((x) => ({ ...x, debt: true })) } : r)),
      };
    });
  },
  async dropBaseline(projectId) {
    await pause();
    update((s) => {
      const baselines = { ...s.baselines };
      delete baselines[projectId];
      return { ...s, baselines, reports: s.reports.map((r) => (r.projectId === projectId ? { ...r, ratchet: undefined, violations: r.violations.map(({ debt: _d, ...x }) => x) } : r)) };
    });
  },
  async dryRun(projectId, text) {
    await pause(900);
    const s = cur();
    const report = s?.reports.find((r) => r.projectId === projectId);
    // The demo cannot read YAML; it reads the packs named on the extends line.
    const ext = /^extends:\s*\[?([^\]\n]*)\]?/m.exec(text)?.[1] ?? 'orgus/recommended';
    const model = modelFromRaw({ extends: ext.split(',').map((x) => x.trim()).filter(Boolean) });
    const eff = effectiveSettings(model, s?.packs ?? []);
    const rules = CURATED.filter((c) => c.kind === 'bool' && eff[`${c.section}.${c.key}`]?.value === true).flatMap((c) => c.rules);
    const now = new Map((report?.rules ?? []).map((r) => [r.rule, r.errors + r.warns]));
    const rows = [...new Set([...rules, ...now.keys()])]
      .filter((r) => !r.startsWith('waivers/') && !r.startsWith('rulebook/'))
      .map((rule) => {
        const files = now.has(rule) ? now.get(rule)! : rules.includes(rule) ? demoFiles(rule) : 0;
        const warn = /tests|stale|any|panic|dangerous/.test(rule);
        return { rule, files, errors: warn ? 0 : files, warns: warn ? files : 0, currentFiles: now.get(rule) ?? 0, sample: Array.from({ length: Math.min(3, files) }, (_, i) => `src/${disciplineRuleInfo(rule).group}/file-${i + 1}.ts`) };
      })
      .sort((a, b) => b.files - a.files || a.rule.localeCompare(b.rule));
    const lost = rows.reduce((n, r) => n + Math.min(20, r.errors * 5 + r.warns), 0);
    return { projectId, problems: /\bstructur:/.test(text) ? ['Unknown section "structur".'] : [], score: Math.max(0, 100 - lost), currentScore: report?.score ?? 100, packs: ['orgus/recommended@1.0.0', ...model.extends.map((x) => `${x}@1.0.0`)], rules: rows, durationMs: 870 };
  },
  async saveRulebook(projectId, text) {
    await pause();
    update((s) => ({ ...s, managed: { ...s.managed, [projectId]: { projectId, text, updatedBy: 'owner', updatedAt: Date.now() } } }));
    return { problems: [] };
  },
  async dropRulebook(projectId) {
    await pause();
    update((s) => {
      const managed = { ...s.managed };
      delete managed[projectId];
      return { ...s, managed };
    });
  },
  async publishPack(p) {
    await pause();
    const out: DisciplinePack = { ...p, kind: 'org', publishedBy: 'owner', publishedAt: Date.now() };
    update((s) => ({ ...s, packs: [...(s.packs ?? []), out] }));
    return out;
  },
  async pin(p) {
    await pause();
    const out: DisciplinePin = { ...p, by: 'owner', at: Date.now() };
    update((s) => ({ ...s, pins: [...(s.pins ?? []).filter((x) => x.pack !== p.pack), out] }));
  },
  async unpin(pack) {
    await pause();
    update((s) => ({ ...s, pins: (s.pins ?? []).filter((x) => x.pack !== pack) }));
  },
  async driftNow(projectId) {
    await pause();
    const ids = projectId ? [projectId] : (cur()?.drift ?? []).map((d) => d.projectId);
    update((s) => ({ ...s, pending: { scans: s.pending?.scans ?? [], drift: ids } }));
    setTimeout(() => update((s) => ({ ...s, drift: (s.drift ?? []).map((d) => (ids.includes(d.projectId) ? { ...d, at: Date.now(), trigger: 'manual' } : d)), pending: { scans: s.pending?.scans ?? [], drift: [] } })), 1800);
  },
  async setDriftSchedule(sch) {
    await pause();
    update((s) => ({ ...s, driftSchedule: sch }));
  },
};

export function useDisciplineActions(): DisciplineActions {
  const mode = useOrgus((s) => s.mode);
  return useMemo(() => (mode === 'mock' ? mock : live), [mode]);
}

/** Whether the missions module is on this hub (Open task creates a backlog item there). */
export function useMissionsPresent(): boolean {
  return useOrgus((s) => s.mode === 'mock' || s.ext.missions !== undefined);
}
